import { cloneDeep, isInteger, omit, pick } from 'lodash'
import { emptyStringToNull } from 'utils-next/api'

// 1 Transform the type to flag all the undesired keys as 'never'.
export type FlagExcludedType<Base> = {
  [Key in keyof Base]: Base[Key] extends Function ? never : Key
}

// 2 Get the keys that are not flagged as 'never'.
export type AllowedNames<Base> = FlagExcludedType<Base>[keyof Base]

// 3 Use this with a simple Pick to get the right interface, excluding the undesired type.
export type OmitType<Base> = Pick<Base, AllowedNames<Base>>

/**
 * Create a type that remove the function fields of the provided type.
 */
export type OmitFunctions<Base> = Pick<Base, AllowedNames<Base>>

/**
 * Create a type that contains all the properties of the entities (excluding the AppEntity fields).
 */
export type EntityProperties<T> = OmitFunctions<Omit<T, 'createdAt' | 'id' | 'updatedAt'>>

export type OmitDeepProperties<T> = T extends object
  ? {
      [K in keyof EntityProperties<T>]: OmitDeepProperties<T[K]>
    }
  : T

export function safePick(
  ...params: Parameters<typeof pick>
): {
  [key: string]: unknown
} {
  return pick(...params)
}

export function valueIsInteger(value: unknown): value is number {
  return isInteger(value)
}

export function strictOmit<T extends object, Key extends keyof T & string>(value: T, params: Key[]) {
  return omit(value, params) as Omit<T, typeof params[number]>
}

export function strictPick<T extends object, Key extends keyof T & string>(value: T, params: Key[]) {
  return pick(value, params) as Pick<T, typeof params[number]>
}

export function emptyStringPropertiesToNull<T extends object>(value: T) {
  const object = cloneDeep(value)

  for (const key in object) {
    object[key] = emptyStringToNull(object[key]) as any
  }

  return object as {
    [key in keyof T]: '' extends T[key] ? Exclude<T[key], ''> | null : T[key]
  }
}

export type Patch<T> = {
  [P in keyof OmitFunctions<Required<T>>]?: undefined extends T[P] ? T[P] | null | undefined : T[P]
}
